import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ThemeProvider } from './contexts/ThemeContext'
import { SidebarProvider } from './contexts/SidebarContext'
import { AuthProvider } from './contexts/AuthContext'
import Layout from './layout/Layout'
import ProtectedRoute from './features/auth/ProtectedRoute'
import Login from '@/pages/Login'
import NotFound from '@/pages/NotFound'
import RouteErrorBoundary from '@/features/errors/RouteErrorBoundary'
import { Loader } from '@/ui'

const Orders = lazy(() => import('./pages/Orders'))
const OrderDetails = lazy(() => import('./pages/OrderDetails'))
const CreateOrder = lazy(() => import('./pages/CreateOrder'))
const SetupMenu = lazy(() => import('./pages/SetupMenu'))
const SetupEventVendors = lazy(() => import('./pages/SetupEventVendors'))
const MasterMenuManagement = lazy(() => import('./pages/MasterMenuManagement'))
const MasterMaterialsManagement = lazy(() => import('./pages/MasterMaterialsManagement'))
const Vendors = lazy(() => import('./pages/Vendors'))
const MenuItemMaterials = lazy(() => import('./pages/MenuItemMaterials'))
const Events = lazy(() => import('./pages/Events'))
const MenuTemplates = lazy(() => import('./pages/MenuTemplates'))
const TemplateItems = lazy(() => import('./pages/TemplateItems'))
const UserProfile = lazy(() => import('./pages/UserProfile'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader />
    </div>
  )
}

function Page({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </RouteErrorBoundary>
  )
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <SidebarProvider>
          <Routes>
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/" element={<Navigate to="/orders" replace />} />
              <Route path="/orders" element={<Page><Orders /></Page>} />
              <Route path="/orders/new" element={<Page><CreateOrder /></Page>} />
              <Route path="/orders/:id" element={<Page><OrderDetails /></Page>} />
              <Route path="/events/:eventId/setup-menu" element={<Page><SetupMenu /></Page>} />
              <Route path="/events/:eventId/setup-vendors" element={<Page><SetupEventVendors /></Page>} />
              <Route path="/master-menu" element={<Page><MasterMenuManagement /></Page>} />
              <Route path="/master-materials" element={<Page><MasterMaterialsManagement /></Page>} />
              <Route path="/vendors" element={<Page><Vendors /></Page>} />
              <Route path="/menu-item-materials/:itemId" element={<Page><MenuItemMaterials /></Page>} />
              <Route path="/events" element={<Page><Events /></Page>} />
              <Route path="/menu-templates" element={<Page><MenuTemplates /></Page>} />
              <Route path="/menu-templates/:templateId" element={<Page><TemplateItems /></Page>} />
              <Route path="/profile" element={<Page><UserProfile /></Page>} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/login" element={<Login />} />
          </Routes>
          <Toaster
            position="bottom-center"
            expand={false}
            closeButton
            gap={8}
            offset={24}
          />
        </SidebarProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App
